import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inviteAPI } from '../api/client';
import { Loading } from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import { apiError, goTo } from '../lib/host';
import '../components/Layout.css';
import AuthShell from '../components/AuthShell';
import './Auth.css';

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { user, loading: authLoading, logout } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    inviteAPI.preview(token)
      .then((res) => setInvite(res.data))
      .catch((err) => setLoadError(apiError(err, 'This invitation is invalid or has expired')));
  }, [token]);

  const accept = async (data) => {
    setBusy(true);
    setError('');
    try {
      const res = await inviteAPI.accept(token, data);
      goTo(res.data.redirect_url);
    } catch (err) {
      setError(apiError(err, 'Could not accept the invitation'));
      setBusy(false);
    }
  };

  if (authLoading || (!invite && !loadError)) return <Loading />;

  const card = (body) => (
    <AuthShell>{body}</AuthShell>
  );

  if (loadError) {
    return card(<><h2>Invitation</h2><div className="error-message">{loadError}</div></>);
  }

  const heading = (
    <>
      <h2>Join {invite.tenant_name}</h2>
      <p className="tcx-muted" style={{ marginBottom: 16 }}>
        Invitation for <strong>{invite.email}</strong> as <span className={`tcx-role tcx-role-${invite.role}`}>{invite.role}</span>
      </p>
      {error && <div className="error-message">{error}</div>}
    </>
  );

  if (user && user.email.toLowerCase() !== invite.email) {
    return card(<>
      {heading}
      <div className="error-message">You're signed in as {user.email}. Log out to accept with {invite.email}.</div>
      <button className="submit-btn" onClick={logout}>Log out</button>
    </>);
  }

  if (user) {
    return card(<>
      {heading}
      <button className="submit-btn" disabled={busy} onClick={() => accept({})}>
        {busy ? 'Joining...' : `Join ${invite.tenant_name}`}
      </button>
    </>);
  }

  if (invite.user_exists) {
    return card(<>
      {heading}
      <p className="tcx-muted" style={{ marginBottom: 16 }}>You already have an account. Log in to accept.</p>
      <button className="submit-btn" onClick={() => navigate(`/login?next=${encodeURIComponent(window.location.href)}`)}>
        Log in
      </button>
    </>);
  }

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  return card(<>
    {heading}
    <form onSubmit={(e) => { e.preventDefault(); accept(form); }}>
      <div className="form-group">
        <label htmlFor="name">Your name</label>
        <input id="name" name="name" value={form.name} onChange={set} required disabled={busy} />
      </div>
      <div className="form-group">
        <label htmlFor="phone">Phone number</label>
        <input id="phone" name="phone" type="tel" value={form.phone} onChange={set} required disabled={busy} />
      </div>
      <div className="form-group">
        <label htmlFor="password">Password</label>
        <input id="password" name="password" type="password" minLength={6} value={form.password} onChange={set} required disabled={busy} />
      </div>
      <button className="submit-btn" disabled={busy}>{busy ? 'Joining...' : 'Create account & join'}</button>
    </form>
  </>);
}
